import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Warehouse, Palette, ArrowRight, BookOpen, ChefHat, TrendingUp, Dna, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { StatCard, Badge } from "@/components/kit";

const OPTIONS = [
  { to: "/create?mode=vibe", icon: Sparkles, title: "Create from a Vibe", body: "Describe the experience you want to serve.", eg: "\u201CUpscale Southern dinner inspired by fall in Georgia.\u201D" },
  { to: "/create?mode=kitchen", icon: Warehouse, title: "Create from My Kitchen", body: "Build from ingredients you have on hand.", eg: "Use inventory first \u2014 reduce waste, boost margin." },
  { to: "/create?mode=theme", icon: Palette, title: "Create from a Theme", body: "Pick a theme and let the engine compose.", eg: "Valentine\u2019s · Steakhouse · Vegan Tasting" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [menus, setMenus] = useState([]);
  const [dna, setDna] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    api.get("/menus").then(({ data }) => setMenus(data.menus || [])).catch(() => {});
    api.get("/recipe-dna").then(({ data }) => setDna(data.dna)).catch(() => {});
    api.get("/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
    api.get("/price-alerts").then(({ data }) => setAlerts(data.alerts || [])).catch(() => {});
  }, []);

  return (
    <div>
      {alerts.length > 0 && (
        <Link to="/purchasing" data-testid="dashboard-price-alert-banner"
          className="flex items-center justify-between gap-3 mb-6 px-5 py-3.5 rounded-sm bg-red-500/10 border border-red-500/30 hover:border-red-500/50 transition-colors">
          <p className="text-sm text-red-200 flex items-center gap-2">
            <AlertTriangle size={16} /> {alerts.length} dish{alerts.length > 1 ? "es" : ""} crossed target food cost after a pricing update.
          </p>
          <span className="text-red-200 text-xs uppercase tracking-widest flex items-center gap-1 shrink-0">Review <ArrowRight size={13} /></span>
        </Link>
      )}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
        <p className="label-eyebrow mb-2">Command Center</p>
        <h1 className="font-serif text-4xl md:text-6xl leading-none">What are we plating<br />today, <span className="gold-text">Chef {user?.name?.split(" ")[0]}?</span></h1>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-4 stagger mb-10">
        {OPTIONS.map((o) => (
          <Link key={o.to} to={o.to} data-testid={`create-option-${o.title.split(" ").pop().toLowerCase()}`}
            className="matte-card p-7 group flex flex-col">
            <div className="h-12 w-12 rounded-sm bg-gold/10 border border-gold/30 flex items-center justify-center text-gold mb-5 group-hover:bg-gold/20 transition-colors">
              <o.icon size={22} strokeWidth={1.6} />
            </div>
            <h3 className="font-serif text-2xl">{o.title}</h3>
            <p className="text-white/55 text-sm mt-2">{o.body}</p>
            <p className="text-white/35 text-xs mt-4 italic flex-1">{o.eg}</p>
            <span className="mt-5 inline-flex items-center gap-2 text-gold text-sm">Start <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
          </Link>
        ))}
      </div>

      {/* Conversational quick input */}
      <div className="matte-card p-6 mb-10 gold-border">
        <p className="label-eyebrow mb-3">Describe what you want to create…</p>
        <QuickCompose onGo={(q) => nav(`/create?mode=vibe&q=${encodeURIComponent(q)}`)} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <StatCard label="Menus" value={analytics?.menus_created ?? "—"} icon={BookOpen} />
            <StatCard label="Recipes" value={analytics?.recipes_created ?? "—"} icon={ChefHat} />
            <StatCard label="Avg Food Cost" value={analytics ? `${analytics.average_food_cost}%` : "—"} accent icon={TrendingUp} />
          </div>
          <div className="matte-card p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="label-eyebrow">Recent Menus</p>
              <Link to="/menus" className="text-gold text-sm hover:underline">View all</Link>
            </div>
            {menus.length === 0 && <p className="text-white/40 text-sm py-6 text-center">No menus yet. Start plating above.</p>}
            <div className="divide-y divide-white/5">
              {menus.slice(0, 5).map((m) => (
                <Link key={m.id} to={`/menus/${m.id}`} className="flex items-center justify-between py-3.5 hover:bg-white/5 -mx-2 px-2 rounded-sm">
                  <div className="min-w-0">
                    <p className="text-white/90 truncate flex items-center gap-2">{m.menu_name} {m.demo && <Badge tone="muted">Demo</Badge>}</p>
                    <p className="text-white/40 text-xs mt-0.5">{m.course_count} courses · {m.cuisine}</p>
                  </div>
                  <span className="font-mono text-gold text-sm shrink-0 ml-3">${m.revenue_potential?.toLocaleString?.() ?? m.revenue_potential}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="matte-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Dna size={18} className="text-gold" strokeWidth={1.6} />
            <p className="label-eyebrow">Your Recipe DNA</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {(dna?.tags || []).map((t) => <Badge key={t}>{t}</Badge>)}
          </div>
          {dna?.top_ingredients?.length > 0 && (
            <div className="mt-5">
              <p className="text-white/40 text-xs mb-2">Frequently used</p>
              <div className="flex flex-wrap gap-2">{dna.top_ingredients.slice(0, 6).map((t) => <Badge key={t} tone="muted">{t}</Badge>)}</div>
            </div>
          )}
          <div className="mt-5 pt-5 border-t border-white/5 flex items-center justify-between">
            <span className="text-white/50 text-sm">Target cost</span>
            <span className="font-mono text-gold">{dna?.target_cost ?? user?.profile?.target_food_cost ?? 28}%</span>
          </div>
          <Link to="/settings" className="btn-outline-gold mt-5 w-full py-3 text-xs uppercase tracking-widest rounded-sm block text-center">Edit DNA</Link>
        </div>
      </div>
    </div>
  );
}

function QuickCompose({ onGo }) {
  const [q, setQ] = useState("");
  return (
    <div className="flex flex-col sm:flex-row gap-3">
      <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="dashboard-quick-input"
        placeholder="e.g. A modern Italian tasting menu for 40 guests…"
        className="flex-1 bg-surface border border-white/10 rounded-sm px-4 py-3.5 text-white/90 outline-none focus:border-gold/60"
        onKeyDown={(e) => e.key === "Enter" && q && onGo(q)} />
      <button onClick={() => q && onGo(q)} data-testid="dashboard-quick-go" className="btn-gold px-8 py-3.5 text-sm uppercase tracking-widest rounded-sm">Compose</button>
    </div>
  );
}
